import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { useGetVerifiedPaymentStateQuery } from "../../../../store/Billing/billingApiSlice";

/* TIP-662 — the landing after a membership payment.
 *
 * Stripe sends the buyer to /dashboard/my-companies?stripe=cs_…&company=…
 * and the page used to ignore both. There was no confirmation of any kind —
 * the receipt email was the only sign the payment had worked — and the list
 * still showed the company as a non-member until a manual reload, which is
 * exactly how someone decides the payment failed and pays a second time.
 *
 * The membership is provisioned asynchronously, from the Stripe webhook, so
 * the data can genuinely not be ready at the moment the buyer arrives. There
 * is nothing to read once; it has to be waited for.
 */

/* ~20s. Long enough for a webhook that is merely slow, short enough that nobody watches a spinner wondering whether it is stuck. Past it the news is still... */
export const POLL_INTERVAL_MS = 2000;
export const MAX_ATTEMPTS = 10;

/* What provisioning looks like when it has landed.
 *
 * _provision_membership writes 'member'. 'verified_paid' is the legacy
 * simulated-pay path's value and no longer reachable from a real Stripe
 * membership payment, but it is accepted because Company.is_member accepts
 * it: a company carrying it IS a member, and waiting for 'member' alone
 * would hang on one until the timeout. VerifyAccountPage still waits on
 * 'verified_paid' ALONE, which is the ~20s spinner noted in TIP-662 — that
 * screen is a separate flow and is not touched here. */
export const PROVISIONED = ["member", "verified_paid"];

export const isProvisioned = (state) =>
  PROVISIONED.includes(state?.company?.subscription_status);

/* idle — not a post-payment landing, the page behaves as it always has waiting — paid, provisioning not yet visible done — membership confirmed slow — gave up... */
export const useMembershipReturn = ({ onProvisioned } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useSelector((state) => state.auth);

  const sessionId = searchParams.get("stripe");
  const companyId = searchParams.get("company");

  /* Read once, on the first render after landing. */
  const [phase, setPhase] = useState(() => (sessionId ? "waiting" : "idle"));
  const [attempts, setAttempts] = useState(0);
  const target = useRef(companyId);

  const polling = phase === "waiting" && Boolean(target.current) && Boolean(token);

  const { data, isFetching } = useGetVerifiedPaymentStateQuery(
    { companyId: target.current },
    {
      skip: !polling,
      pollingInterval: polling ? POLL_INTERVAL_MS : 0,
      refetchOnMountOrArgChange: true,
    }
  );

  /* The URL is cleaned once, the moment the wait is over either way.
     `replace` so the parameterised URL leaves the history altogether. */
  const clearParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("stripe");
        next.delete("company");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  /* A landing with no company to ask about. Older sessions, created before
     the return URL carried the id, arrive this way. The payment still
     happened, so it is still worth saying so — just without the wait. */
  useEffect(() => {
    if (phase === "waiting" && !target.current) {
      setPhase("slow");
      clearParams();
    }
  }, [phase, clearParams]);

  useEffect(() => {
    if (phase !== "waiting" || !data) return;

    if (isProvisioned(data)) {
      setPhase("done");
      clearParams();
      /* The list is holding a copy of the membership from before the
         payment; without this the card stays stale, which is half the bug. */
      onProvisioned?.();
      return;
    }

    /* Counted on answers, not on a clock, so a slow endpoint cannot burn the
       budget while a request is still in flight. */
    if (isFetching) return;
    setAttempts((n) => {
      const next = n + 1;
      if (next >= MAX_ATTEMPTS) {
        setPhase("slow");
        clearParams();
        /* Re-read anyway: provisioning may have landed between the last
           answer and giving up. */
        onProvisioned?.();
      }
      return next;
    });
  }, [data, isFetching, phase, clearParams, onProvisioned]);

  return { phase, isReturning: phase !== "idle", sessionId };
};

export default useMembershipReturn;
